<template>
  <dara-row class="d-flex align-items-center">
    <dara-col
      :sm="24"
      :md="4">
      <dara-user-link :id="user.id">
        <dara-user-badge v-if="badge" :badge="badge" :size="60" offset-y="9.8" offset-x="1.2" />
        <dara-image
          fit="cover"
          :src="image"
          :height="135"
          :width="135"
          class="p--lg el-avatar--circle" />
      </dara-user-link>
    </dara-col>
    <dara-col
      :sm="24"
      :md="editing ? 16 : 22">
      <div class="mb--sm">
        <dara-small>{{ followers }}</dara-small>
      </div>
      <dara-header v-if="!user.blocked" size="sm" class="d-flex align-items-center">
        <dara-user-link :id="user.id">
          {{ user.name }} {{ user.last_name }}
        </dara-user-link>
        <dara-button
          v-if="showFollowButton(user)"
          class="ms-3"
          size="xs"
          :plain="true"
          @click="onFollowButtonClick">
          {{ following ? $t('unfollow') : $t('follow') }}
        </dara-button>
      </dara-header>
      <dara-header v-else size="sm" class="color--info">
        <dara-user-link :id="user.id">
          {{ user.name }} {{ user.last_name }}
        </dara-user-link>
      </dara-header>
      <dara-p
        v-truncate="1"
        color="regular">
        {{ user.biography }}
      </dara-p>
      <div>
        <dara-small>{{ user.position }} • {{ departmentName }}</dara-small>
      </div>
    </dara-col>
    <dara-col
      v-if="editing"
      :sm="24"
      :md="4"
      class="text-end">
      <div class="mt-2">
        <el-dropdown v-if="canEditUser(user)" trigger="click">
          <dara-icon
            icon="three-dots-vertical"
            class="cursor--pointer mx--md" />
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item>
              <dara-user-link :id="user.id" action="edit">
                <dara-icon size="sm" icon="pencil" />
                {{ $t('edit') }}
              </dara-user-link>
            </el-dropdown-item>
            <div v-if="canBlockUser(user)">
              <el-dropdown-item v-if="user.blocked">
                <a @click="onUnblockUser(user)">
                  <dara-icon size="sm" icon="check-square" />
                  {{ $t('unblock') }}
                </a>
              </el-dropdown-item>
              <el-dropdown-item v-if="!user.blocked">
                <a @click="onBlockUser(user)">
                  <dara-icon size="sm" icon="x-square" />
                  {{ $t('block') }}
                </a>
              </el-dropdown-item>
            </div>
            <div v-if="canDeleteUser(user)">
              <el-dropdown-item>
                <a @click="setDeleteUser(user)">
                  <dara-icon size="sm" icon="trash" />
                  {{ $t('delete') }}
                </a>
              </el-dropdown-item>
            </div>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </dara-col>
  </dara-row>
</template>

<script>
import { toastError } from '@/services/toastService';
import { getImageFromAssets } from '@/helpers/images';
import { getters } from '@/modules/user/user.store';
import { NO_ARTICLE_IMAGE } from '@/modules/article/article.constants';
import { getDepartmentById } from '@/modules/department/department.service';
import { translate } from '@/services/translate.service';
import { getFollowers, unblockUser, blockUser, followUser, unfollowUser, getFollowingStatus } from '@/modules/user/user.service';

import DaraUserBadge from '@/modules/user/components/UserBadge.vue';
import DaraUserLink from '@/modules/user/components/UserLink.vue';

export default {
  name: 'UserCard',
  components: {
    DaraUserBadge,
    DaraUserLink,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: true,
    },
    bestWriterId: {
      type: String,
      default: '',
    },
    topWriterId: {
      type: String,
      default: '',
    },
    topReaderId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      currentUser: getters.getUser(),
      following: false,
      loading: true,
      departmentName: null,
      numberFollowers: null,
    };
  },
  computed: {
    image() {
      return (this.user.profile_pic) ? this.user.profile_pic : getImageFromAssets(NO_ARTICLE_IMAGE);
    },
    followers() {
      if (this.numberFollowers > 1) {
        return `${this.numberFollowers} ${translate('followers')}`;
      }
      return `${this.numberFollowers} ${translate('follower')}`;
    },
    badge() {
      if (this.user.id === this.bestWriterId) return 'best-writer';
      if (this.user.id === this.topWriterId) return 'top-writer';
      if (this.user.id === this.topReaderId) return 'top-reader';
      return false;
    },
  },
  mounted() {
    this.getFollowers();
    this.getFollowingStatus();
    this.getDepartmentName();
  },
  methods: {
    getDepartmentName() {
      if (this.user.department_id) {
        getDepartmentById(this.user.department_id)
          .then(data => {
            this.departmentName = `${data.name}`;
          }).catch();
      }
    },
    getFollowers() {
      getFollowers(this.user)
        .then(response => {
          this.numberFollowers = response.length;
        })
        .catch(() => toastError(`${translate('error-on-getting-followers')}`));
    },
    getFollowingStatus() {
      this.loading = true;
      getFollowingStatus(this.user)
        .then(response => {
          this.following = response;
        })
        .catch(() => toastError(`${translate('error-getting-the-following-status')}`))
        .finally(() => {
          this.loading = false;
        });
    },
    setDeleteUser(user) {
      this.$emit('setDeleteUser', user);
    },
    onUnblockUser(user) {
      unblockUser(user).then(() => this.$router.go(0));
    },
    onBlockUser(user) {
      blockUser(user).then(() => this.$router.go(0));
    },
    canEditUser(user) {
      return (this.currentUser.permission > 1 && user.permission < 3) || (this.currentUser.permission > 1 && user.id === this.currentUser.id);
    },
    canBlockUser(user) {
      return (this.currentUser.permission > 1 && user.permission < 3);
    },
    canDeleteUser(user) {
      return (this.currentUser.permission > 1 && user.id !== this.currentUser.id);
    },
    isSameUser(user) {
      return this.currentUser.id === user.id;
    },
    showFollowButton(user) {
      return !this.loading && !this.isSameUser(user);
    },
    onFollowButtonClick() {
      return this.following ? this.onUnfollowUser() : this.onFollowUser();
    },
    onFollowUser() {
      followUser(this.user)
        .then(() => {
          this.following = true;
          this.numberFollowers++;
        })
        .catch(() => toastError(`${translate('error-on-following-user')}`));
    },
    onUnfollowUser() {
      unfollowUser(this.user)
        .then(() => {
          this.following = false;
          this.numberFollowers--;
        })
        .catch(() => toastError(`${translate('error-on-unfollowing-user')}`));
    },
  },
};
</script>
